import React, { useState, useCallback } from 'react';
import { View, ScrollView, TouchableOpacity, ActivityIndicator, Image as RNImage, Alert } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useQuery } from '@tanstack/react-query';
import { useFocusEffect } from '@react-navigation/native';
import dayjs from 'dayjs';

import tw from '../../../../styles/tailwind';
import Text from '../../../../components/Text';
import Icon from '../../../../components/IcomoonIcon';
import IconButton from '../../../../components/IconButton';
import api from '../../../../utils/api';


function parseLocation(description?: string | null): { location: string | null; body: string } {
  if (!description) return { location: null, body: '' };
  const match = description.match(/^📍\s*(.+?)\n\n([\s\S]*)$/);
  if (match) return { location: match[1].trim(), body: match[2].trim() };
  return { location: null, body: description };
}


export default function GuardTicketDetailScreen({ route, navigation }: any) {
  const { communityId, ticketUuid } = route.params;
  const [submitting, setSubmitting] = useState(false);

  const ticketQuery = useQuery<any>({
    queryKey: ['guard.tickets.detail', communityId, ticketUuid],
    queryFn: () => api.get(`/communities/${communityId}/tickets/${ticketUuid}`).then(r => r.data),
    enabled: !!communityId && !!ticketUuid,
  });

  useFocusEffect(useCallback(() => {
    ticketQuery.refetch();
  }, [communityId, ticketUuid]));

  const ticket = ticketQuery.data;

  const markCompleted = async () => {
    if (!ticket) return;
    if (ticket.status === 'atendida') return;

    setSubmitting(true);
    try {
      await api.patch(`/communities/${communityId}/tickets/${ticketUuid}/status`, {
        status: 'atendida',
      });
      ticketQuery.refetch();
    } catch (e: any) {
      Alert.alert('Error', e?.response?.data?.message ?? 'No se pudo actualizar el estado');
    } finally {
      setSubmitting(false);
    }
  };

  if (!ticket) {
    return (
      <View style={tw`flex-1 items-center justify-center bg-white`}>
        <ActivityIndicator color={tw.color('primary')} />
      </View>
    );
  }

  const { location, body } = parseLocation(ticket.description);
  const publicComments: any[] = ticket.public_comments ?? ticket.publicComments ?? [];
  const isCompleted = ticket.status === 'atendida';
  const assignedAt = ticket.created_at;

  return (
    <View style={tw`flex-1 bg-background`}>
      <SafeAreaView edges={['top']} style={tw`bg-white`}>
        <View style={tw`row items-center px-5 py-3`}>
          <TouchableOpacity onPress={() => navigation.goBack()} style={tw`mr-3`}>
            <Icon name="chevron-left" color={tw.color('gray-700')} size={20} />
          </TouchableOpacity>
          <Text style={tw`text-base flex-1 font-500`} numberOfLines={1}>{ticket.folio}</Text>
        </View>
      </SafeAreaView>

      <ScrollView contentContainerStyle={tw`p-4 pb-40`}>
        <View style={tw`bg-white rounded-xl p-4 mb-4`}>
          <Text style={tw`text-lg font-500 mb-2`}>{ticket.category?.name ?? 'Incidencia'}</Text>

          {ticket.is_high_priority && (
            <View style={tw`row self-start bg-red-50 px-2.5 py-1 rounded-full mb-2`}>
              <Text style={tw`text-red-600 text-xs font-500`}>‼ Alta prioridad</Text>
            </View>
          )}

          {location && (
            <View style={tw`row items-center mb-1`}>
              <Icon name="pin" color={tw.color('gray-400')} size={14} />
              <Text style={tw`text-gray-500 ml-1`}>{location}</Text>
            </View>
          )}

          <Text style={tw`text-gray-500 text-sm`}>
            Asignado {dayjs(assignedAt).calendar
              ? dayjs(assignedAt).format('D MMM · HH:mm')
              : dayjs(assignedAt).format('D MMM · HH:mm')}
          </Text>
        </View>

        {body.length > 0 && (
          <>
            <Text style={tw`text-gray-700 font-500 mb-2 px-1`}>Descripción</Text>
            <View style={tw`bg-white rounded-xl p-4 mb-4`}>
              <Text style={tw`text-sm text-gray-700`}>{body}</Text>
            </View>
          </>
        )}

        {ticket.files?.length > 0 && (
          <ScrollView horizontal contentContainerStyle={tw`gap-2 mb-4`} showsHorizontalScrollIndicator={false}>
            {ticket.files.map((f: any) => (
              <RNImage key={f.uuid} source={{ uri: f.url }} style={tw`w-32 h-32 rounded-lg`} />
            ))}
          </ScrollView>
        )}

        {publicComments.length > 0 && (
          <>
            <Text style={tw`text-gray-700 font-500 mb-2 px-1`}>Indicaciones</Text>
            <View style={tw`bg-primary/10 rounded-xl p-4 mb-4`}>
              {publicComments.map((c: any, idx: number) => (
                <View key={c.uuid} style={tw.style(idx > 0 && `mt-3 pt-3 border-t border-primary/20`)}>
                  <Text style={tw`text-xs text-gray-500 mb-1`}>
                    {c.user?.name} · {dayjs(c.created_at).format('D MMM · HH:mm')}
                  </Text>
                  <Text style={tw`text-sm text-gray-800`}>{c.body}</Text>
                </View>
              ))}
            </View>
          </>
        )}
      </ScrollView>

      <SafeAreaView edges={['bottom']} style={tw`bg-white border-t border-gray-200`}>
        <View style={tw`px-5 py-3`}>
          {isCompleted ? (
            <View style={tw`bg-emerald-50 rounded-full py-3 items-center`}>
              <Text style={tw`text-emerald-700 font-500`}>Incidencia completada</Text>
            </View>
          ) : (
            <IconButton
              title="Marcar como completada"
              disabled={submitting}
              onPress={markCompleted}
            />
          )}
        </View>
      </SafeAreaView>
    </View>
  );
}
